/**
 * OrderTimelineDrawer — Phase 6 `/sale` drill-down.
 *
 * Right-hand drawer overlay that explains a single order end-to-end:
 * 5-event timeline (Order Created → First KOT → Last KOT → Bill → Closed)
 * plus the Phase 2 delay split (prep / dining / ordering_span / billing /
 * settlement). When KOT data is absent for the order, the KOT events are
 * greyed out and prep/dining durations render as "—" (never "0 min").
 *
 * Contract:
 *   <OrderTimelineDrawer orderId={...} open={bool} onClose={fn} />
 *
 * The caller controls open/close — this component only fetches when
 * `orderId` changes while `open` is true.
 */
import React, { useEffect, useState } from 'react';
import { orderTimelineApi } from '../lib/api';
import { toast } from 'sonner';
import {
  X, Info, CheckCircle, Circle, Clock, Receipt, ForkKnife,
} from '@phosphor-icons/react';

const EVENT_ICON = {
  order_created: ForkKnife,
  first_kot: Clock,
  last_kot: Clock,
  bill_printed: Receipt,
  closed: CheckCircle,
};

const fmtMin = (v) => (v == null ? '—' : `${Number(v).toFixed(0)} min`);
const fmtINR = (v) => {
  if (v == null) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  if (n >= 1_00_000) return `₹${(n / 1_00_000).toFixed(2)}L`;
  if (n >= 1_000) return `₹${(n / 1_000).toFixed(1)}K`;
  return `₹${n.toFixed(0)}`;
};
const fmtTime = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false });
  } catch {
    return '—';
  }
};

const TimelineEvent = ({ ev, idx, prevTime }) => {
  const Icon = EVENT_ICON[ev.event] || Circle;
  const missing = ev.missing;
  const gap = !missing && prevTime && ev.time
    ? Math.round((new Date(ev.time) - new Date(prevTime)) / 60000)
    : null;
  return (
    <div className="flex gap-3" data-testid={`order-tl-event-${ev.event}`}>
      <div className="flex flex-col items-center">
        <div
          className={`w-8 h-8 rounded-full flex items-center justify-center border ${
            missing
              ? 'border-white/10 bg-white/[0.02] text-zinc-600'
              : 'border-[#D4FF00]/30 bg-[#D4FF00]/10 text-[#D4FF00]'
          }`}
        >
          <Icon size={14} weight="duotone" />
        </div>
        {idx < 4 && <div className="w-[2px] flex-1 bg-white/5 min-h-[40px]" />}
      </div>
      <div className="flex-1 pb-5">
        <div className="flex items-center justify-between">
          <p className={`text-[13px] font-medium ${missing ? 'text-zinc-500' : 'text-zinc-100'}`}>
            {ev.label}
          </p>
          <p className={`text-[13px] font-mono tabular-nums ${missing ? 'text-zinc-600' : 'text-zinc-300'}`}>
            {fmtTime(ev.time)}
          </p>
        </div>
        {gap != null && (
          <p className="text-[11px] text-zinc-500 mt-0.5">+{gap} min from previous</p>
        )}
        {missing && (
          <p className="text-[10px] text-amber-500/70 mt-1 uppercase tracking-wider">
            Timestamp not captured
          </p>
        )}
      </div>
    </div>
  );
};

const DelayRow = ({ label, value, sub, accent }) => (
  <div className="flex items-center justify-between py-2">
    <div>
      <p className="text-[12px] text-zinc-400">{label}</p>
      {sub && <p className="text-[10px] text-zinc-600 mt-0.5">{sub}</p>}
    </div>
    <p className={`text-[16px] font-mono tabular-nums ${accent || 'text-zinc-200'}`}>
      {fmtMin(value)}
    </p>
  </div>
);

export default function OrderTimelineDrawer({ orderId, open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !orderId) return;
    let alive = true;
    setLoading(true);
    setError(null);
    orderTimelineApi.get(orderId)
      .then((res) => { if (alive) setData(res.data); })
      .catch((err) => {
        if (!alive) return;
        const msg = err.response?.data?.detail || err.message || 'Failed to load';
        setError(String(msg));
        toast.error(`Could not load order ${orderId}`);
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [orderId, open]);

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const timeline = data?.timeline || [];
  const delays = data?.delays || {};
  const meta = data?.meta || {};

  return (
    <>
      {/* Scrim */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
        onClick={onClose}
        data-testid="order-tl-scrim"
      />
      {/* Drawer */}
      <aside
        className="fixed right-0 top-0 bottom-0 w-full md:w-[540px] bg-[#0A0D12] border-l border-white/5 z-50 overflow-y-auto"
        data-testid="order-timeline-drawer"
      >
        <div className="sticky top-0 bg-[#0A0D12] border-b border-white/5 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <p className="text-[10px] uppercase tracking-[0.08em] text-zinc-500">Order Drill-Down</p>
            <h2 className="text-[18px] font-semibold text-zinc-100 mt-0.5">#{orderId}</h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg border border-white/10 hover:bg-white/5 flex items-center justify-center text-zinc-400"
            data-testid="order-tl-close"
            aria-label="Close"
          >
            <X size={14} />
          </button>
        </div>

        {loading && (
          <div className="p-6 space-y-3 animate-pulse" data-testid="order-tl-loading">
            <div className="h-4 bg-white/5 rounded w-1/2" />
            <div className="h-4 bg-white/5 rounded w-3/4" />
            <div className="h-32 bg-white/5 rounded mt-4" />
          </div>
        )}

        {error && (
          <div className="p-6" data-testid="order-tl-error">
            <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4">
              <p className="text-red-400 font-semibold mb-1">Could not load order</p>
              <p className="text-[13px] text-zinc-400">{error}</p>
            </div>
          </div>
        )}

        {data && !loading && (
          <div className="p-6 space-y-6">
            {/* Header facts */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-zinc-500">Table</p>
                <p className="text-[15px] text-zinc-100 font-mono tabular-nums mt-0.5">{data.table}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-zinc-500">Server</p>
                <p className="text-[15px] text-zinc-100 mt-0.5 truncate">{data.server || '—'}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-zinc-500">Total</p>
                <p className="text-[15px] text-emerald-400 font-mono tabular-nums mt-0.5">
                  {fmtINR(data.final_total)}
                </p>
              </div>
            </div>

            {/* KOT-confidence caveat */}
            {meta.kot_data_confidence !== 'high' && (
              <div
                className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-3 flex items-start gap-2"
                data-testid="order-tl-kot-caveat"
              >
                <Info size={14} className="text-amber-400 shrink-0 mt-0.5" weight="duotone" />
                <div className="text-[11px] text-zinc-400">
                  KOT data confidence: <span className="font-mono text-amber-400">{meta.kot_data_confidence}</span>
                  {' · '}
                  {meta.items_with_kot}/{meta.items_total} items have KOT timestamps.
                  {(meta.limitations || []).length > 0 && (
                    <p className="mt-1">{meta.limitations[0]}</p>
                  )}
                </div>
              </div>
            )}

            {/* 5-event timeline */}
            <div data-testid="order-tl-events">
              <h3 className="text-[11px] uppercase tracking-wider text-zinc-500 mb-3">Timeline</h3>
              {timeline.map((ev, i) => (
                <TimelineEvent
                  key={ev.event}
                  ev={ev}
                  idx={i}
                  prevTime={i > 0 ? timeline[i - 1].time : null}
                />
              ))}
            </div>

            {/* Delays block — Phase 2 formulas */}
            <div data-testid="order-tl-delays" className="rounded-xl bg-white/[0.02] border border-white/5 p-4">
              <h3 className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Delays</h3>
              <DelayRow
                label="Prep Time"
                value={delays.prep_min}
                sub="order created → first KOT"
                accent={delays.prep_min != null && delays.prep_min > 25 ? 'text-amber-400' : null}
              />
              <div className="border-t border-white/5" />
              <DelayRow
                label="Dining Time"
                value={delays.dining_min}
                sub="first KOT → bill printed"
                accent={delays.dining_min != null && delays.dining_min > 60 ? 'text-amber-400' : null}
              />
              <div className="border-t border-white/5" />
              <DelayRow
                label="Ordering Span"
                value={delays.ordering_span_min}
                sub="first KOT → last KOT · behavioural signal"
              />
              <div className="border-t border-white/5" />
              <DelayRow
                label="Settlement Delay"
                value={delays.settlement_delay_min}
                sub="bill printed → closed"
                accent={delays.settlement_delay_min != null && delays.settlement_delay_min > 15 ? 'text-red-400' : null}
              />
              <div className="border-t border-white/5 mt-1" />
              <DelayRow
                label="Total Turnaround"
                value={delays.total_turnaround_min}
                sub="order created → closed"
                accent="text-[#D4FF00]"
              />
            </div>

            {/* Items */}
            {(data.items || []).length > 0 && (
              <div data-testid="order-tl-items">
                <h3 className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Items</h3>
                <div className="rounded-xl bg-white/[0.02] border border-white/5 overflow-hidden">
                  {data.items.map((it, i) => (
                    <div
                      key={i}
                      className="px-3 py-2 border-b border-white/5 last:border-b-0 flex items-center justify-between text-[12px]"
                    >
                      <div>
                        <p className="text-zinc-200">{it.item_name}</p>
                        {(it.kot_group || it.kot_number) && (
                          <p className="text-[10px] text-zinc-500 mt-0.5 font-mono">
                            KOT {it.kot_number || '—'} · {it.kot_group || 'unassigned'}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="font-mono text-zinc-300 tabular-nums">×{it.quantity}</p>
                        <p className="text-[10px] text-zinc-500 font-mono mt-0.5">
                          {it.kot_fired_at ? fmtTime(it.kot_fired_at) : '—'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Formula reference — small footer */}
            <p className="text-[10px] text-zinc-600 italic border-t border-white/5 pt-3">
              {meta.formula_reference}
            </p>
          </div>
        )}
      </aside>
    </>
  );
}
